import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { jobberMasterApi, jobberSlug } from '@/services/jobberMasterApi';

/** Jobber combobox — only ACTIVE Jobber Master jobbers. testIds: `<testId>-trigger / -search / -option-<slug> / -empty`.
 *  `preferCategory` lists jobbers of that category first, tagged. */
export const JobberPicker = ({ value, onChange, testId = 'jobber', placeholder = 'Select jobber…', allowClear = false, preferCategory }) => {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  useEffect(() => { jobberMasterApi.active().then(setRows).catch(() => setRows([])); }, []);
  const has = (j) => !!preferCategory && (j.categories || []).includes(preferCategory);
  const list = preferCategory ? [...(rows || [])].sort((a, b) => Number(has(b)) - Number(has(a))) : rows || [];
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid={`${testId}-trigger`}>
          <span className={`truncate ${value ? '' : 'text-gray-400'}`}>{value || placeholder}</span>
          <ChevronsUpDown className="w-4 h-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[280px]" align="start">
        <Command>
          <CommandInput placeholder="Search jobber or place…" data-testid={`${testId}-search`} />
          <CommandList>
            <CommandEmpty>
              <span data-testid={`${testId}-empty`}>{rows?.length === 0 ? 'No active jobbers yet' : 'No jobber found'}</span>
              <Link to="/admin/jobber-master" className="block mt-1 text-xs text-[#2D5F4F] underline" data-testid={`${testId}-master-link`}>Open Jobber Master</Link>
            </CommandEmpty>
            {allowClear && value && (
              <CommandItem value="__none__" onSelect={() => { onChange(''); setOpen(false); }} className="text-gray-500" data-testid={`${testId}-clear`}>
                <X className="w-4 h-4 mr-2 shrink-0" />No jobber
              </CommandItem>
            )}
            {list.map((j) => (
              <CommandItem key={j.id} value={`${j.name} ${j.place || ''}`} onSelect={() => { onChange(j.name); setOpen(false); }} data-testid={`${testId}-option-${jobberSlug(j.name)}`}>
                <Check className={`w-4 h-4 mr-2 shrink-0 ${value === j.name ? 'opacity-100' : 'opacity-0'}`} />
                <span className="min-w-0 flex-1"><span className="block truncate">{j.name}</span>
                  <span className="block truncate text-[11px] text-gray-500">{[j.place, j.pincode, j.gstin === 'URP' ? 'Unregistered' : j.gstin].filter(Boolean).join(' · ')}</span></span>
                {has(j) && <span className="ml-2 shrink-0 rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">{preferCategory}</span>}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default JobberPicker;
